/**
 * The seven stages of the AI analysis pipeline, named once. The Overview pipeline reads its stage
 * names, order and figures from here; the Alert Flow columns and the campaign screen are to read the
 * same, so a stage says the same thing wherever it appears (client change of 2026-10-30).
 *
 * Pure: it takes the counters and returns the stages. The campaign screen passes the counters of
 * one campaign, the Overview passes everyone's; the stages come out the same shape.
 */
import type { FunnelCounters } from './funnel';

export const PIPELINE_STAGE_IDS = [
  'ingest',
  'dedup',
  'score',
  'classify',
  'consolidate',
  'plan',
  'execute',
] as const;

export type PipelineStageId = (typeof PIPELINE_STAGE_IDS)[number];

export const PIPELINE_STAGE_TITLES: Record<PipelineStageId, string> = {
  ingest: 'Ingest + Normalize',
  dedup: 'Dedup',
  score: 'Cluster + Score',
  classify: 'Classify LLM/RAG',
  consolidate: 'Consolidate',
  plan: 'Plan',
  execute: 'Execute + Validate',
};

/** A count the stage holds back or sends another way, beside the figure that goes on. */
export interface StageSplit {
  key: string;
  label: string;
  value: number;
  /** `need`: it waits on a person (honey, DESIGN.md colour rule 2); `bad`: it failed. Absent: neutral. */
  tone?: StageTone;
}

export type StageTone = 'need' | 'bad';

export interface PipelineStage {
  id: PipelineStageId;
  /** 1 to 7, the order the work flows. */
  index: number;
  title: string;
  /** The stage's figure, and what it counts. */
  value: number;
  unit: string;
  /** What does not go on the main way at this stage. A count that is zero is left out unless it is always shown. */
  splits: StageSplit[];
  /** One line in plain words under the figures; empty when the splits say it all. */
  note: string;
  p50: string;
  confidence?: string;
  /** The strongest tone among the splits: the stage is marked when something in it waits or failed. */
  attention?: StageTone;
}

/** A count as the console writes it: thousands separated. */
export const formatCount = (value: number) => value.toLocaleString('en-US');
const n = formatCount;

/** A split that stays on screen at zero, because "0 escalated" is information. */
const always = (key: string, label: string, value: number): StageSplit => ({ key, label, value });
const whenAny = (key: string, label: string, value: number, tone?: StageTone): StageSplit[] =>
  value > 0 ? [{ key, label, value, ...(tone ? { tone } : {}) }] : [];
/** A split that is always shown, and takes its tone only when it counts something. */
const toned = (key: string, label: string, value: number, tone: StageTone): StageSplit =>
  value > 0 ? { key, label, value, tone } : { key, label, value };

export function buildPipelineStages(c: FunnelCounters): PipelineStage[] {
  const anomalies = c.verdicts.threat + c.verdicts.suppressed + c.verdicts.escalated;
  const stages: Omit<PipelineStage, 'index' | 'title' | 'p50' | 'attention'>[] = [
    {
      id: 'ingest',
      value: c.events.total,
      unit: 'security events',
      splits: [
        ...whenAny('inFlight', 'in flight', c.candidates.pending + c.candidates.analyzing),
        ...whenAny('errors', 'errors', c.candidates.error, 'bad'),
      ],
      note: `${n(c.candidates.registered)} candidates registered`,
    },
    {
      id: 'dedup',
      value: c.candidates.unique,
      unit: 'unique',
      splits: [always('duplicated', 'duplicated', c.candidates.duplicated)],
      note: `${n(c.events.duplicate)} event dups`,
    },
    {
      id: 'score',
      value: anomalies,
      unit: 'anomalies',
      splits: [always('baseline', 'baseline normal', c.verdicts.baseline)],
      note: 'related alerts grouped and scored before the AI review',
    },
    {
      id: 'classify',
      value: c.verdicts.threat,
      unit: 'threats',
      splits: [
        always('suppressed', 'FP suppressed', c.verdicts.suppressed),
        toned('escalated', 'escalated', c.verdicts.escalated, 'need'),
      ],
      note: '',
      confidence: '—',
    },
    {
      id: 'consolidate',
      value: c.campaigns.total,
      unit: 'campaigns',
      splits: [always('standalone', 'standalone', c.threats.standalone)],
      note: 'campaign of one is valid',
    },
    {
      id: 'plan',
      value: c.actions.total,
      unit: 'remediations',
      splits: [
        toned('pending', 'pending', c.actions.pending, 'need'),
        always('confirmed', 'confirmed', c.actions.confirmed),
      ],
      note: '',
    },
    {
      id: 'execute',
      value: c.actions.completed,
      unit: 'completed',
      splits: [
        always('inProgress', 'in progress', c.actions.inProgress),
        ...whenAny('reverted', 'reverted', c.actions.reverted),
        ...whenAny('cancelled', 'cancelled', c.actions.cancelled),
      ],
      note: 'rollback not measured yet',
    },
  ];
  return stages.map((stage, i) => {
    const tones = stage.splits.map((split) => split.tone);
    const attention: StageTone | undefined = tones.includes('bad') ? 'bad' : tones.includes('need') ? 'need' : undefined;
    return {
      ...stage,
      index: i + 1,
      title: PIPELINE_STAGE_TITLES[stage.id],
      p50: '—',
      ...(attention ? { attention } : {}),
    };
  });
}
